import { envString } from "../utils/config";

/** Minimal typed fetch wrapper for the CODE APE backend API. */
export function useApi() {
  const baseUrl = getApiBaseUrl();
  const inDev = import.meta.env.DEV === true;

  async function request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    if (!baseUrl) throw new Error("Backend API is not configured for this deployment.");
    const requestPath = method === "GET" ? withCacheBust(path) : path;
    const res = await fetch(`${baseUrl}${requestPath}`, {
      method,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...headers
      },
      body: body ? JSON.stringify(body) : undefined
    });
    if (!res.ok) {
      const text = await res.text().catch(() => `HTTP ${res.status}`);
      let msg = `HTTP ${res.status}`;
      try { msg = JSON.parse(text).error ?? msg; } catch { if (inDev) msg = text || msg; }
      throw new Error(inDev ? msg : safeClientError(msg));
    }
    return res.json() as Promise<T>;
  }

  return {
    get: <T = unknown>(path: string, headers?: Record<string, string>) =>
      request<T>("GET", path, undefined, headers),
    post: <T = unknown>(path: string, body?: unknown, headers?: Record<string, string>) =>
      request<T>("POST", path, body, headers)
  };
}


function getApiBaseUrl(): string | null {
  const configured = envString("VITE_API_URL");
  if (configured) return configured.replace(/\/+$/, "");
  return import.meta.env.PROD ? "/api" : "http://localhost:3001";
}

function safeClientError(message: string): string {
  if (/sk-|Bearer\s+|eyJh|https?:\/\/|rpc|alchemy|infura|pinata|kms|secret|private/i.test(message)) {
    return "Request failed";
  }
  return message.length > 180 ? "Request failed" : message;
}

function withCacheBust(path: string): string {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}_=${Date.now()}`;
}
